import React, { FocusEvent, MouseEvent, PropsWithChildren, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import Popover from '../../../../../functional-components/functional-foundations/src/components/Popover'
import jss from './style'
import SimpleColor from '../../helpers/types/SimpleColor.interface'

const useStyles = makeStyles(jss)

const isNode = (value: EventTarget | null): value is Node => {
    return value instanceof Node
}

interface PropsInterface {
    top?: boolean;
    bottom?: boolean;
    right?: boolean;
    left?: boolean;
    position?: 'top' | 'bottom' | 'right' | 'left';
    color?: SimpleColor;
    value: string;
    className?: string;
    'data-testid'?: string;
}

/**
 *
 */
const Infotip = ({
    top,
    bottom,
    right,
    left,
    position = 'right',
    color = 'neutral',
    children,
    value,
    className = '',
    'data-testid': dataTestId = 'infotip'

}: PropsWithChildren<PropsInterface>): JSX.Element => {
    const styles = useStyles({ value })
    const triggerRef = useRef<HTMLDivElement | null>(null)
    const popupRef = useRef<HTMLDivElement | null>(null)
    const [open, setOpen] = useState(false)

    const tPosition = useMemo(() => {
        let placement = position

        if (top) {
            placement = 'top'
        } else if (bottom) {
            placement = 'bottom'
        } else if (left) {
            placement = 'left'
        } else if (right) {
            placement = 'right'
        }

        return placement
    }, [bottom, left, position, right, top])

    const handleOpen = () => {
        if (value) setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
        const nextElement = isNode(event.relatedTarget) ? event.relatedTarget : null

        if (nextElement && popupRef.current?.contains(nextElement)) return

        handleClose()
    }

    const handlePopupMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
        const nextElement = isNode(event.relatedTarget) ? event.relatedTarget : null

        if (nextElement && triggerRef.current?.contains(nextElement)) return

        handleClose()
    }

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        const nextElement = isNode(event.relatedTarget) ? event.relatedTarget : null

        if (nextElement && triggerRef.current?.contains(nextElement)) return
        if (nextElement && popupRef.current?.contains(nextElement)) return

        handleClose()
    }

    const isOpen = Boolean(value && open && triggerRef.current)

    return <>
        <div
            ref={triggerRef}
            className={styles.infotip}
            onMouseEnter={handleOpen}
            onMouseLeave={handleMouseLeave}
            onFocus={handleOpen}
            onBlur={handleBlur}
            data-testid={dataTestId}
        >
            {children}
        </div>
        <Popover
            open={isOpen}
            anchorEl={triggerRef.current}
            placement={tPosition}
            onClose={handleClose}
            data-testid={`${dataTestId}-popover`}
        >
            <div
                ref={popupRef}
                className={`${styles.popup} infotip infotip--${tPosition} infotip--${color} ${className}`}
                onMouseEnter={handleOpen}
                onMouseLeave={handlePopupMouseLeave}
                data-testid={`${dataTestId}-popup`}
            >
                {value}
            </div>
        </Popover>
    </>
}

export default Infotip
